import { requireEstDoctorSelection } from './utils/estDoctorGuard'
// TB-093: clinic versions of the pages the establishment shares with the doctor app
import { estOr, estDirect, skipForEstView, isEstView } from './views/Establishment/estRoute'
export default [
  {
    // =============================================================================
    // Doctor
    // =============================================================================
    path: '/doctor',
    component: () => import('./layouts/doctor/Main.vue'),
    children: [
      // =============================================================================
      // Theme Routes
      // =============================================================================
      {
        path: 'login',
        name: 'doctor-login',
        component: () => import('./views/Doctor/Login.vue'),
        meta: {
          isHome: false,
          title: 'LoginPage'
        }
      },
      {
        path: 'signup',
        name: 'doctor-signup',
        component: () => import('./views/Doctor/SignUp.vue'),
        meta: {
          isHome: false,
          title: 'Sign up'
        }
      },
      {
        path: 'signup-information',
        name: 'doctor-signup-information',
        component: () => import('./views/Doctor/SignUpInformation.vue'),
        meta: {
          isHome: false,
          title: 'Sign Up Information'
        }
      },
      {
        path: 'signup-information-establishment',
        name: 'doctor-signup-information-establishment',
        component: () => import('./views/Doctor/SignUpInformationEstablishment.vue'),
        meta: {
          isHome: false,
          title: 'Sign Up Information Establishment'
        }
      },
      {
        path: 'signup-establishment',
        name: 'doctor-signup-establishment',
        component: () => import('./views/Doctor/SignUpEstablishment.vue'),
        meta: {
          isHome: false,
          title: 'Establishment Signup'
        }
      },
      {
        path: 'verification',
        name: 'doctor-verification',
        component: () => import('./views/Doctor/Verification.vue'),
        meta: {
          isHome: false,
          title: 'UserVerification'
        }
      },
      {
        path: 'verification-establishment',
        name: 'doctor-verification-establishment',
        component: () => import('./views/Doctor/VerificationEstablishment.vue'),
        meta: {
          isHome: false,
          title: 'Establishment Verification'
        }
      },
      {
        path: 'forgot-password',
        name: 'doctor-forgot-password',
        component: () => import('./views/Doctor/ForgotPassword.vue'),
        meta: {
          isHome: false,
          title: 'Forgot Password'
        }
      },
      {
        path: 'forgot-password-verification',
        name: 'doctor-forgot-password-verification',
        component: () => import('./views/Doctor/ForgotPasswordVerification.vue'),
        meta: {
          isHome: false,
          title: 'Forgot Password Verification'
        }
      },
      {
        path: 'reset-password',
        name: 'doctor-reset-password',
        component: () => import('./views/Doctor/ResetPassword.vue'),
        meta: {
          isHome: false,
          title: 'Reset Password'
        }
      },
      // TB-081 (DEF-174): invited doctor sets a first password from the
      // e-mail link (?token=). Public: reachable while logged out.
      {
        path: 'set-password',
        name: 'doctor-set-password',
        component: () => import('./views/Doctor/SetPassword.vue'),
        meta: {
          isHome: false,
          public: true,
          title: 'Set Password'
        }
      },
      {
        path: "/doctor/reschedule",
        name: "doctor-reschedule",
        component: () => import("./views/Doctor/RescheduleSlot.vue"),
        meta: {
          role: "provider",
          isHome: false,
          title: "Reschedule Slot"
        }
      },

      // TB-073: /doctor is the doctor dashboard; the month calendars that
      // used to be the doctor home moved to their own page /doctor/calendar.
      {
        path: '',
        name: 'doctor-home',
        component: () => import('./views/Doctor/Dashboard.vue'),
        meta: {
          isHome: false,
          title: 'Today',
          ownNewMenu: true // TB-118: the Today header has the '+ New' menu
        }
      },
      // TB-118: menu entry points for the grouped pages (tabs in the shell)
      {
        path: 'settings',
        name: 'doctor-settings',
        redirect: '/doctor/profile'
      },
      {
        path: 'earnings',
        name: 'doctor-earnings',
        redirect: '/doctor/my-earnings'
      },
      {
        path: 'notification-preferences',
        name: 'doctor-notification-preferences',
        redirect: { name: 'doctor-reminders', query: { tab: 'preferences' } }
      },
      {
        path: 'insights',
        name: 'doctor-insights',
        component: () => import('./views/Doctor/Insights.vue'),
        meta: {
          isHome: false,
          title: 'Insights'
        }
      },
      // TB-109 (A): one schedule screen (week + month, day editor, appointment
      // panel). The old month overview now opens it; Week view and Create slot
      // stay reachable until the new screen has run for a while.
      {
        path: 'schedule',
        name: 'doctor-schedule',
        // TB-109-C: in clinic mode the same screen comes with a doctor picker
        component: estOr(() => import('./views/Doctor/Schedule/ScheduleScreen.vue'), () => import('./views/Establishment/Schedules.vue')),
        beforeEnter: skipForEstView(requireEstDoctorSelection),
        meta: {
          isHome: false,
          title: 'Schedule',
          estTitle: 'Schedule'
        }
      },
      {
        path: 'calendar',
        name: 'doctor-calendar',
        redirect: (to) => ({ path: '/doctor/schedule', query: to.query })
      },
      {
        path: 'change-password',
        name: 'doctor-change-password',
        component: () => import('./views/Doctor/ChangePassword.vue'),
        meta: {
          isHome: false,
          title: 'Change Password'
        }
      },
      {
        path: 'consultation',
        name: 'doctor-consultation',
        component: estOr(() => import('./views/Doctor/Consultation.vue'), () => import('./views/Establishment/Bookings.vue')),
        meta: {
          isHome: false,
          role: 'provider',
          title: 'Requests', // TB-109 (A): same word as the menu
          estTitle: 'Bookings' // TB-093: clinic wording for the top bar
        }
      },
      {
        path: "/doctor/consultation-details/:ID",
        name: "doctor-consultation-details",
        component: estOr(() => import('./views/Doctor/ConsultationDetails.vue'), estDirect(() => import('./views/Doctor/ConsultationDetails.vue'), { subtitle: 'Booking, patient and consultation notes.' })),
        meta: {
          role: "provider",
          isHome: false,
          title: "Consultation Details",
          estTitle: "Booking" // TB-093: clinic wording for the top bar
        }
      },
      {
        path: 'establishment-doctor',
        name: 'doctor-establishment-doctor',
        // TB-073: establishment dashboard; ?view=doctors shows the full doctors table
        component: () => import('./views/Establishment/Home.vue'),
        meta: {
          isHome: false,
          title: 'Today',
          ownNewMenu: true // TB-118
        }
      },
      {
        path: 'add-doctor',
        name: 'doctor-add-doctor',
        component: () => import('./views/Doctor/AddDoctor.vue'),
        meta: {
          isHome: false,
          title: 'Add Doctor'
        }
      },
      {
        path: 'create-slot',
        name: 'doctor-create-slot',
        component: estOr(() => import('./views/Doctor/CreateSlot.vue'), estDirect(() => import('./views/Doctor/CreateSlot.vue'), { subtitle: 'Open bookable time for the selected doctor.' })),
        beforeEnter: requireEstDoctorSelection, // TB-086 (DEF-240)
        meta: {
          isHome: false,
          title: 'Create Slot'
        }
      },
      {
        path: "/doctor/invoice/:ID",
        name: "doctor-invoice",
        component: estOr(() => import('./views/Doctor/Invoice.vue'), estDirect(() => import('./views/Doctor/Invoice.vue'), { subtitle: 'Invoice for a booking with one of your doctors.' })),
        meta: {
          role: "provider",
          isHome: false,
          title: "Invoice"
        }
      },
      {
        path: '/doctor/invoices',
        name: 'doctor-invoices',
        // TB-106: a clinic gets its own Invoices list (every status, its doctors)
        component: estOr(() => import('./views/Doctor/Invoices.vue'), () => import('./views/Establishment/Invoices.vue')),
        meta: {
          role: 'provider',
          isHome: false,
          title: 'Invoices'
        }
      },
      {
        path: 'book-appoinment',
        name: 'doctor-book-appoinment',
        component: () => import('./views/Doctor/BookAppoinment.vue'),
        meta: {
          isHome: false,
          title: 'Book Appointment'
        },
      },
      {
        path: 'patients',
        name: 'doctor-patients',
        component: () => import('./views/Doctor/Patients.vue'),
        meta: {
          isHome: false,
          title: 'Patients'
        }
      },
      {
        path: "/doctor/patient-profile/:ID",
        name: "doctor-patient-profile",
        component: () => import("./views/Doctor/PatientProfile.vue"),
        meta: {
          role: "provider",
          isHome: false,
          title: "Patient Profile"
        }
      },
      {
        path: "/doctor/patients-previuos-details/:ID",
        name: "doctor-patients-previuos-details",
        component: () => import("./views/Doctor/PatientsPreviuosDetails.vue"),
        meta: {
          role: "provider",
          isHome: false,
          title: "Patients Previous Details"
        }
      },
      {
        path: "/doctor/patients-prescription/:ID",
        name: "doctor-patients-prescription-details",
        component: () => import("./views/Doctor/Prescriptions.vue"),
        meta: {
          role: "provider",
          isHome: false,
          title: "Patients Prescription"
        }
      },
      {
        path: "/doctor/notifications",
        name: "doctor-notifications",
        component: () => import("./views/User/Notifications.vue"),
        meta: {
          role: "provider",
          isHome: false,
          title: "Notifications"
        }
      },

      {
        path: 'profile',
        name: 'doctor-profile',
        component: estOr(() => import('./views/Doctor/Profile.vue'), estDirect(() => import('./views/Doctor/Profile.vue'), { subtitle: 'Your clinic details, locations, consultation purposes and tax.' })),
        meta: {
          isHome: false,
          title: 'Profile'
        }
      },
      {
        path: 'profile/edit',
        name: 'doctor-profile-edit',
        component: estOr(() => import('./views/Doctor/EditProfile.vue'), estDirect(() => import('./views/Doctor/EditProfile.vue'), { subtitle: 'Update the name, contact details and photo of your clinic.' })),
        meta: {
          title: 'Edit Profile'
        }
      },
      {
        path: 'poc',
        name: 'doctor-poc',
        component: () => import('./views/Doctor/Poc.vue'),
        meta: {
          isHome: false,
          title: 'Purpose of Consultation'
        }
      },
      {
        path: 'subscriptions',
        name: 'doctor-subscriptions',
        component: estOr(() => import('./views/Doctor/Subscriptions.vue'), () => import('./views/Establishment/Subscription.vue')),
        meta: {
          isHome: false,
          title: 'Subscriptions',
          estTitle: 'Subscription' // TB-093: clinic wording for the top bar
        }
      },
      {
        path: 'my-earnings',
        name: 'doctor-my-earnings',
        component: estOr(() => import('./views/Doctor/MyEarnings.vue'), () => import('./views/Establishment/Earnings.vue')),
        meta: {
          isHome: false,
          title: 'My Earnings',
          estTitle: 'Earnings' // TB-093: clinic wording for the top bar
        }
      },
      {
        path: 'chat',
        name: 'doctor-chat',
        component: () => import('./views/Doctor/Chat.vue'),
        meta: {
          isHome: false,
          title: 'Chat'
        }
      },
      {
        path: 'notifications-setting',
        name: 'doctor-notifications-setting',
        component: estOr(() => import('./views/Doctor/NotificationsSetting.vue'), estDirect(() => import('./views/Doctor/NotificationsSetting.vue'), { subtitle: 'Messages sent to patients before and after their appointments.' })),
        meta: {
          isHome: false,
          title: 'Notifications Setup',
          estTitle: 'Notification setup' // TB-093: clinic wording for the top bar
        }
      },
      // TB-081 (DEF-176): establishment invitations for the signed-in doctor.
      // TB-131: the page is now My clinics (invitations, practices, leave).
      {
        path: 'clinics',
        name: 'doctor-clinics',
        component: () => import('./views/Doctor/Invitations.vue'),
        meta: {
          isHome: false,
          title: 'My clinics'
        }
      },
      {
        path: 'invitations',
        name: 'doctor-invitations',
        redirect: '/doctor/clinics'
      },
      {
        path: 'reminders',
        name: 'doctor-reminders',
        // TB-111 / TB-112: one page for the doctor's and the clinic's own reminder and notification copies.
        component: () => import('./views/Doctor/RemindersNotifications.vue'),
        meta: {
          isHome: false,
          title: 'Reminders & notifications'
        }
      },
      // {
      //   path: '/doctor/add-payment-card',
      //   name: 'doctor-payment-card',
      //   component: () => import('./views/Doctor/AddPaymentCard.vue'),
      //   meta: {
      //     isHome: false,
      //     title: 'Add Payment Card'
      //   }
      // },
      {
        path: 'add-payment-card',
        name: 'doctor-payment-card',
        component: estOr(() => import('./views/Doctor/AddPaymentCard.vue'), estDirect(() => import('./views/Doctor/AddPaymentCard.vue'), { subtitle: 'Pay for your plan by card.' })),
        meta: {
          isHome: false,
          title: 'Add Payment Card'
        }
      },
      {
        // TB-119: Stats became Insights; old links keep working
        path: 'stats',
        name: 'doctor-stats',
        redirect: (to) => ({ path: '/doctor/insights', query: to.query })
      },
      {
        path: 'payouts',
        name: 'doctor-payouts',
        component: estOr(() => import('./views/Doctor/Payouts.vue'), () => import('./views/Establishment/Payouts.vue')),
        meta: {
          isHome: false,
          title: 'Payouts'
        }
      },
      {
        path: 'calendar-view',
        name: 'doctor-calendar-view',
        // TB-093: in clinic mode Schedules lets the clinic pick a doctor (was a
        // redirect to the Doctors list when none was chosen).
        // TB-109-C: a doctor (or a clinic viewing one) gets the new schedule
        // screen; the legacy CalendarView is no longer routed.
        component: () => import('./views/Establishment/Schedules.vue'),
        beforeEnter: (to) => (isEstView() ? true : { path: '/doctor/schedule', query: to.query }),
        meta: {
          isHome: false,
          title: 'Calendar View',
          estTitle: 'Schedule' // TB-093: clinic wording for the top bar
        }
      },
      {
        path: '/doctor/help-contact-us',
        name: 'doctor-help-contact',
        component: () => import('./views/User/HelpAndContact.vue'),
        meta: {
          role: "provider",
          isHome: false,
          title: 'Help And Contact'
        }
      },
      {
        path: 'about',
        name: 'doctor-about',
        component: () => import('./views/pages/AboutUs.vue'),
        meta: {
          isHome: false,
          title: 'About Us'
        }
      },
      {
        path: '/doctor/:id/call',
        name: 'doctor-call',
        component: () => import('./views/Doctor/Session.vue'),
        meta: {
          role: 'provider',
          isHome: false,
          title: 'Call'
        }
      },
      {
        path: 'faqs',
        name: 'doctor-faqs',
        component: () => import('@/views/User/FAQs.vue'),
        meta: {
          isHome: false,
          title: 'FAQs'
        }
      },
      {
        path: 'privacy-policy',
        name: 'doctor-privacy-policy',
        component: () => import('@/views/User/PrivacyPolicy.vue'),
        meta: {
          isHome: false,
          title: 'Privacy Policy'
        }
      },
      {
        path: 'termsandconditions',
        name: 'doctor-terms-and-conditions',
        component: () => import('@/views/User/TermsAndConditions.vue'),
        meta: {
          isHome: false,
          title: 'Terms & Conditions'
        }
      },
      {
        path: 'refundandreturnpolicy',
        name: 'doctor-refund-and-return-policy',
        component: () => import('@/views/User/RefundAndReturnPolicy.vue'),
        meta: {
          isHome: false,
          title: 'Refund & Return Policy'
        }
      }
    ]

  }
]


